/** Signing in, the guest terminal, forgotten passwords, registration, reset links and changing a password. */
import { PASSWORD_MIN, type Me, type SiteInfo } from '@imagesync/core';
import { get, post, setCsrf } from './api.js';
import { S } from './state.js';
import { $, $$, DOT, esc, openDialog, closeDialog, setErr, toast } from './util.js';

let onSignedIn: (m: Me) => Promise<void> = async () => undefined;
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');
const officeOf = (slug: string) => S.site?.offices.find(o => o.slug === slug);
const chosen = () => ($('#siOffice') as HTMLSelectElement).value || S.site?.defaultOffice || '';

function show(view: 'signin' | 'reset' | 'app' | 'boot'): void {
  $('#bootView').hidden = view !== 'boot';
  $('#signinView').hidden = view !== 'signin';
  $('#resetView').hidden = view !== 'reset';
  $('#app').hidden = view !== 'app';
}

function renderOffice(): void {
  const o = officeOf(chosen());
  $('#siGuestWrap').hidden = !o?.guestLogin;
  $('#siRegisterBtn').hidden = !o?.registration;
  const c = o?.contact;
  $('#siContact').textContent = c && (c.phone || c.desk) ? 'Help with an account: ' + [c.desk, c.phone, c.hours].filter(Boolean).join(' ' + DOT + ' ') : '';
  $('#siSub').textContent = o ? o.name : '';
}
function form(which: 'si' | 'fg' | 'rg'): void {
  $('#siForm').hidden = which !== 'si';
  $('#fgForm').hidden = which !== 'fg';
  $('#rgForm').hidden = which !== 'rg';
  $('#siGuestWrap').hidden = which !== 'si' || !officeOf(chosen())?.guestLogin;
  $('.signin-card .foot').hidden = which !== 'si';
  $('#siTitle').textContent = which === 'si' ? 'Sign in' : which === 'fg' ? 'Reset your password' : 'Register for an account';
  const first = which === 'si' ? '#siUser' : which === 'fg' ? '#fgLogin' : '#rgFirst';
  ($(first) as HTMLInputElement).focus();
}

export function showSignin(site: SiteInfo, message?: string): void {
  show('signin');
  const sel = $('#siOffice') as HTMLSelectElement;
  sel.innerHTML = site.offices.map(o => '<option value="' + esc(o.slug) + '">' + esc(o.name) + '</option>').join('');
  const want = new URLSearchParams(location.search).get('office');
  sel.value = site.offices.some(o => o.slug === want) ? want! : site.defaultOffice;
  $('#siOfficeField').hidden = site.offices.length < 2;
  $('#siSample').hidden = !site.tenant.sample;
  document.title = 'Sign in · ' + site.tenant.name + ' · ImageSync';
  renderOffice();
  form('si');
  setErr($('#siErr'), message ?? '');
}

async function signedIn(m: Me): Promise<void> {
  setCsrf(m.csrf);
  ($('#siPass') as HTMLInputElement).value = '';
  show('app');
  document.title = (S.site?.tenant.name ?? '') + ' · ImageSync';
  await onSignedIn(m);
}

export function showReset(): void {
  show('reset');
  const welcome = new URLSearchParams(location.search).get('welcome') === '1';
  $('#rsTitle').textContent = welcome ? 'Choose your password' : 'Choose a new password';
  ($('#rsPass') as HTMLInputElement).focus();
}

export function wireSignin(h: { signedIn: (m: Me) => Promise<void> }): void {
  onSignedIn = h.signedIn;
  $('#siOffice').addEventListener('change', renderOffice);
  $('#siForm').addEventListener('submit', async e => {
    e.preventDefault();
    const err = $('#siErr'), username = ($('#siUser') as HTMLInputElement).value.trim(), password = ($('#siPass') as HTMLInputElement).value;
    if (!username || !password) { setErr(err, 'Enter your username and password.'); return; }
    setErr(err, '');
    const btn = $('#siForm button[type="submit"]') as HTMLButtonElement;
    btn.disabled = true;
    try { await signedIn(await post<Me>('/api/v1/auth/login', { username, password, office: chosen() })); }
    catch (x) { setErr(err, errMsg(x)); ($('#siPass') as HTMLInputElement).select(); }
    finally { btn.disabled = false; }
  });
  $('#siGuest').addEventListener('click', async () => {
    try { await signedIn(await post<Me>('/api/v1/auth/guest', { office: chosen() })); } catch (x) { setErr($('#siErr'), errMsg(x)); }
  });
  $('#siForgotBtn').addEventListener('click', () => { ($('#fgLogin') as HTMLInputElement).value = ($('#siUser') as HTMLInputElement).value; $('#fgOk').hidden = true; setErr($('#fgErr'), ''); form('fg'); });
  $('#fgBack').addEventListener('click', () => form('si'));
  $('#fgForm').addEventListener('submit', async e => {
    e.preventDefault();
    const login = ($('#fgLogin') as HTMLInputElement).value.trim();
    if (!login) { setErr($('#fgErr'), 'Enter your username or email address.'); return; }
    setErr($('#fgErr'), '');
    try {
      const r = await post<{ message: string }>('/api/v1/auth/forgot', { login, office: chosen() });
      $('#fgOk').textContent = r.message; $('#fgOk').hidden = false;
    } catch (x) { setErr($('#fgErr'), errMsg(x)); }
  });
  $('#siRegisterBtn').addEventListener('click', () => { setErr($('#rgErr'), ''); form('rg'); });
  $('#rgBack').addEventListener('click', () => form('si'));
  $('#rgForm').addEventListener('submit', async e => {
    e.preventDefault();
    const v = (id: string) => ($('#' + id) as HTMLInputElement).value.trim();
    const body = { office: chosen(), first: v('rgFirst'), last: v('rgLast'), org: v('rgOrg'), email: v('rgEmail'), username: v('rgUser').toLowerCase(), password: ($('#rgPass') as HTMLInputElement).value };
    const err = $('#rgErr');
    if (!body.first || !body.last) { setErr(err, 'Enter your first and last name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(body.email)) { setErr(err, 'Enter a valid email address.'); return; }
    if (!/^[a-z0-9._-]{3,60}$/.test(body.username)) { setErr(err, 'Choose a username of 3 to 60 letters, numbers, dots, dashes or underscores.'); return; }
    if (body.password.length < PASSWORD_MIN) { setErr(err, 'Use at least ' + PASSWORD_MIN + ' characters for the password.'); return; }
    setErr(err, '');
    try { await signedIn(await post<Me>('/api/v1/auth/register', body)); toast('Welcome. Your account is ready.'); } catch (x) { setErr(err, errMsg(x)); }
  });

  $('#rsForm').addEventListener('submit', async e => {
    e.preventDefault();
    const a = ($('#rsPass') as HTMLInputElement).value, b = ($('#rsPass2') as HTMLInputElement).value, err = $('#rsErr');
    if (a.length < PASSWORD_MIN) { setErr(err, 'Use at least ' + PASSWORD_MIN + ' characters.'); return; }
    if (a !== b) { setErr(err, 'The two passwords don’t match.'); return; }
    setErr(err, '');
    const token = new URLSearchParams(location.search).get('token') ?? '';
    try {
      await post('/api/v1/auth/reset', { token, password: a });
      $('#rsForm').hidden = true; $('#rsOk').hidden = false;
      history.replaceState(null, '', '/reset');   // the token is spent; keep it out of the address bar
    } catch (x) { setErr(err, errMsg(x)); }
  });

  const pw = $('#pwDlg') as HTMLDialogElement;
  $('#pwBtn').addEventListener('click', () => { $('#acctMenu').hidden = true; for (const el of $$<HTMLInputElement>('#pwForm input')) el.value = ''; setErr($('#pwErr'), ''); openDialog(pw); ($('#pwCur') as HTMLInputElement).focus(); });
  pw.addEventListener('click', e => { if ((e.target as HTMLElement).closest('[data-close]')) closeDialog(pw); });
  $('#pwForm').addEventListener('submit', async e => {
    e.preventDefault();
    const cur = ($('#pwCur') as HTMLInputElement).value, a = ($('#pwNew') as HTMLInputElement).value, b = ($('#pwNew2') as HTMLInputElement).value, err = $('#pwErr');
    if (!cur) { setErr(err, 'Enter your current password.'); return; }
    if (a.length < PASSWORD_MIN) { setErr(err, 'Use at least ' + PASSWORD_MIN + ' characters.'); return; }
    if (a !== b) { setErr(err, 'The two new passwords don’t match.'); return; }
    try { await post('/api/v1/auth/password', { current: cur, next: a }); closeDialog(pw); toast('Password changed. Other sessions of this account are signed out.'); }
    catch (x) { setErr(err, errMsg(x)); }
  });
}

/** Where the app starts: a reset link, the sign-in screen, or straight in with a live session. */
export async function boot(): Promise<void> {
  show('boot');
  let site: SiteInfo;
  try { site = await get<SiteInfo>('/api/v1/site'); }
  catch (e) { $('#bootView').innerHTML = '<div class="signin-card"><h1>ImageSync</h1><p class="sub">' + esc(errMsg(e)) + '</p></div>'; return; }
  S.site = site;
  if (location.pathname === '/reset') { showReset(); return; }
  const m = await get<Me>('/api/v1/auth/me').catch(() => null);
  if (m?.signedIn) await signedIn(m); else showSignin(site);
}
export const backToSignin = (message: string) => { if (S.site) { S.me = null; showSignin(S.site, message); } };
